/**
 * language-registry-service.ts
 *
 * Dynamic language registry, native Intl formatting, ICU MessageFormat pluralization,
 * 5-tier translation fallback chains, and locality auto-detection.
 */

import { UserLocaleSettings, MultilingualAgentTrace } from './locale-contracts.js';
import { normalizeBcp47Locale } from './locale-detector.js';
import { globalLocaleRegistry } from './locale-registry.js';

export interface LanguageDefinition {
  readonly code: string;       // ISO 639-1 e.g. "en", "zh", "sw", "lg", "ar", "fr", "es", "de"
  readonly shortCode: string;  // Uppercase 2-letter e.g. "EN", "ZH", "SW", "LG", "AR", "FR", "ES", "DE"
  readonly name: string;       // Native display name e.g. "English", "简体中文", "Kiswahili", "Oluganda", "العربية"
  readonly flagEmoji: string;  // Country/regional flag emoji e.g. "🇺🇸", "🇨🇳"
  readonly defaultLocale: string; // e.g. "en-US", "zh-CN", "sw-TZ", "lg-UG", "ar-EG"
  readonly direction: 'ltr' | 'rtl';
  readonly isEnabled: boolean;
}

export class LanguageRegistryService {
  private static instance: LanguageRegistryService | null = null;
  private languages: Map<string, LanguageDefinition> = new Map();
  private translationBundles: Map<string, Record<string, string>> = new Map();
  private userLocaleSettings: UserLocaleSettings;
  private agentTraces: MultilingualAgentTrace[] = [];

  private constructor() {
    // Default active languages
    const defaults: LanguageDefinition[] = [
      { code: 'en', shortCode: 'EN', name: 'English (US)', flagEmoji: '🇺🇸', defaultLocale: 'en-US', direction: 'ltr', isEnabled: true },
      { code: 'zh', shortCode: 'ZH', name: '简体中文', flagEmoji: '🇨🇳', defaultLocale: 'zh-CN', direction: 'ltr', isEnabled: true },
      { code: 'es', shortCode: 'ES', name: 'Español', flagEmoji: '🇪🇸', defaultLocale: 'es-ES', direction: 'ltr', isEnabled: true },
      { code: 'fr', shortCode: 'FR', name: 'Français', flagEmoji: '🇫🇷', defaultLocale: 'fr-FR', direction: 'ltr', isEnabled: true },
      { code: 'de', shortCode: 'DE', name: 'Deutsch', flagEmoji: '🇩🇪', defaultLocale: 'de-DE', direction: 'ltr', isEnabled: true },
      { code: 'ar', shortCode: 'AR', name: 'العربية', flagEmoji: '🇸🇦', defaultLocale: 'ar-EG', direction: 'rtl', isEnabled: true },
      { code: 'sw', shortCode: 'SW', name: 'Kiswahili', flagEmoji: '🇹🇿', defaultLocale: 'sw-TZ', direction: 'ltr', isEnabled: true },
      { code: 'lg', shortCode: 'LG', name: 'Oluganda', flagEmoji: '🇺🇬', defaultLocale: 'lg-UG', direction: 'ltr', isEnabled: true },
    ];

    defaults.forEach((l) => this.languages.set(l.code, l));

    // Load persisted enabled locales from localStorage
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('mesh_enabled_locales');
        if (saved) {
          const enabledCodes: string[] = JSON.parse(saved);
          if (Array.isArray(enabledCodes)) {
            for (const [code, def] of this.languages.entries()) {
              this.languages.set(code, {
                ...def,
                // English is always kept enabled by default
                isEnabled: code === 'en' ? true : enabledCodes.includes(code),
              });
            }
          }
        }
      }
    } catch {
      // Ignore storage read errors
    }

    // Seed English baseline bundles
    this.translationBundles.set('en', {
      'app.title': 'Sovereign Asset Defense Mesh',
      'nav.scrum': 'Engineering Backlog',
      'nav.crm': 'Sales Pipeline',
      'nav.wiki': 'Knowledge Wiki',
      'cms.publish.button': 'Publish Page',
      'cms.draft.button': 'Save Draft',
      'common.task_count': '{count, plural, one{# active task} other{# active tasks}}',
      'common.deal_count': '{count, plural, one{# open deal} other{# open deals}}',
    });

    // Seed Kiswahili translations
    this.translationBundles.set('sw', {
      'app.title': 'Mtandao wa Ulinzi wa Rasilimali',
      'nav.scrum': 'Uhandisi na Kazi',
      'nav.crm': 'Mauzo na Mapato',
      'nav.wiki': 'Maktaba ya Ujuzi',
      'cms.publish.button': 'Chapisha Ukurasa',
      'cms.draft.button': 'Hifadhi Rasimu',
      'common.task_count': '{count, plural, one{kazi # inayoendelea} other{kazi # zinazoendelea}}',
    });

    // Seed Arabic (RTL) translations
    this.translationBundles.set('ar', {
      'app.title': 'شبكة الدفاع عن الأصول السيادية',
      'nav.scrum': 'مهام الهندسة',
      'nav.crm': 'خط المبيعات',
      'nav.wiki': 'موسوعة المعرفة',
      'cms.publish.button': 'نشر الصفحة',
      'cms.draft.button': 'حفظ المسودة',
      'common.task_count': '{count, plural, zero{لا توجد مهام} one{مهمة واحدة} two{مهمتان} few{# مهام} many{# مهمة} other{# مهمة}}',
    });

    let initialLang = 'en';
    let initialLocale = 'en-US';
    let initialDir: 'ltr' | 'rtl' = 'ltr';

    try {
      if (typeof localStorage !== 'undefined') {
        const savedPref = localStorage.getItem('mesh_locale_preference');
        if (savedPref) {
          const parsed = JSON.parse(savedPref);
          if (parsed && parsed.locale) {
            const normalized = normalizeBcp47Locale(parsed.locale);
            const found = Array.from(this.languages.values()).find(
              (l) => l.code === normalized || normalizeBcp47Locale(l.code) === normalized || normalizeBcp47Locale(l.defaultLocale) === normalized
            );
            if (found) {
              initialLang = found.code;
              initialLocale = found.defaultLocale;
              initialDir = found.direction;
            }
          }
        }
      }
    } catch {
      // Ignore storage errors
    }

    this.userLocaleSettings = {
      language: initialLang,
      locale: initialLocale,
      direction: initialDir,
      timezone: 'UTC',
      currency: 'USD',
      numberFormat: 'standard',
    };
  }

  public static getInstance(): LanguageRegistryService {
    if (!LanguageRegistryService.instance) {
      LanguageRegistryService.instance = new LanguageRegistryService();
    }
    return LanguageRegistryService.instance;
  }

  public getActiveLanguages(): readonly LanguageDefinition[] {
    return Array.from(this.languages.values()).filter((l) => l.isEnabled);
  }

  public getAllLanguages(): readonly LanguageDefinition[] {
    return Array.from(this.languages.values());
  }

  public getEnabledLocales(): string[] {
    return Array.from(this.languages.values())
      .filter((l) => l.isEnabled)
      .map((l) => l.code);
  }

  public isValidBcp47(code: string): boolean {
    return /^[a-z]{2,3}(-[A-Za-z0-9]+)?$/.test(code);
  }

  public provisionLanguage(
    codeOrDef: string | LanguageDefinition,
    partialDef?: Partial<LanguageDefinition>
  ): LanguageDefinition {
    const code = typeof codeOrDef === 'string' ? codeOrDef : codeOrDef.code;
    const def: Partial<LanguageDefinition> = typeof codeOrDef === 'string' ? (partialDef || {}) : codeOrDef;

    if (!this.isValidBcp47(code)) {
      throw new Error(`Invalid BCP 47 language code: "${code}". Must match /^[a-z]{2,3}(-[A-Za-z0-9]+)?$/`);
    }

    const direction = def.direction || (['ar', 'he', 'fa', 'ur'].includes(code.split('-')[0]) ? 'rtl' : 'ltr');
    const fullDef: LanguageDefinition = {
      code,
      shortCode: def.shortCode || code.toUpperCase().slice(0, 2),
      flagEmoji: def.flagEmoji || '🌐',
      name: def.name || code.toUpperCase(),
      defaultLocale: def.defaultLocale || (code.includes('-') ? code : `${code}-${code.toUpperCase()}`),
      direction,
      isEnabled: def.isEnabled !== undefined ? def.isEnabled : true,
    };

    this.languages.set(code, fullDef);
    this.persistEnabledLocales();
    return fullDef;
  }

  public toggleLanguage(code: string, enabled: boolean): void {
    const lang = this.languages.get(code);
    if (!lang) {
      throw new Error(`Language "${code}" not found in registry.`);
    }

    this.languages.set(code, {
      ...lang,
      isEnabled: enabled,
    });

    this.persistEnabledLocales();
  }

  private persistEnabledLocales(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        const enabledCodes = this.getEnabledLocales();
        localStorage.setItem('mesh_enabled_locales', JSON.stringify(enabledCodes));
      }
    } catch {
      // Ignore storage persistence errors
    }
  }

  public getUserLocaleSettings(): UserLocaleSettings {
    return this.userLocaleSettings;
  }

  public setUserLanguage(langCode: string, syncRegistry = true): UserLocaleSettings {
    const normalized = normalizeBcp47Locale(langCode);
    let lang = this.languages.get(langCode) || this.languages.get(normalized) || this.languages.get(langCode.split('-')[0]);
    if (!lang) {
      for (const item of this.languages.values()) {
        if (
          item.defaultLocale.toLowerCase() === langCode.toLowerCase() ||
          item.code.toLowerCase() === langCode.toLowerCase() ||
          normalizeBcp47Locale(item.code) === normalized ||
          normalizeBcp47Locale(item.defaultLocale) === normalized
        ) {
          lang = item;
          break;
        }
      }
    }

    if (!lang || !lang.isEnabled) {
      return this.userLocaleSettings;
    }

    this.userLocaleSettings = {
      ...this.userLocaleSettings,
      language: lang.code,
      locale: lang.defaultLocale,
      direction: lang.direction,
    };

    // Update document HTML dir attribute if DOM is present
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', lang.direction);
      document.documentElement.setAttribute('lang', lang.code);
    }

    // Keep globalLocaleRegistry in sync
    try {
      if (syncRegistry && globalLocaleRegistry.getLocale() !== normalized) {
        globalLocaleRegistry.setLocale(normalized);
      }
    } catch {
      // Ignore
    }

    return this.userLocaleSettings;
  }

  /**
   * Auto-detects user preferred language from browser locality headers / navigator.languages.
   */
  public detectLocalityPreference(clientLanguages: readonly string[] = []): string {
    const candidateList =
      clientLanguages.length > 0
        ? clientLanguages
        : typeof navigator !== 'undefined' && navigator.languages
        ? navigator.languages
        : ['en'];

    for (const raw of candidateList) {
      const primary = raw.split('-')[0].toLowerCase();
      const match = this.languages.get(primary);
      if (match && match.isEnabled) {
        return match.code;
      }
    }
    return 'en';
  }

  /**
   * 5-Tier Fallback Chain Resolver with ICU MessageFormat Pluralization.
   */
  public translate(
    key: string,
    params: { count?: number; [key: string]: any } = {},
    options: {
      userLang?: string;
      tenantDefault?: string;
      pluginDefault?: string;
    } = {}
  ): string {
    const userLang = options.userLang || this.userLocaleSettings.language;
    const tenantDefault = options.tenantDefault || 'en';
    const pluginDefault = options.pluginDefault || 'en';

    // 1. Try User Language
    let template = this.translationBundles.get(userLang)?.[key];

    // 2. Try Tenant Default
    if (!template && tenantDefault) {
      template = this.translationBundles.get(tenantDefault)?.[key];
    }

    // 3. Try Plugin Default
    if (!template && pluginDefault) {
      template = this.translationBundles.get(pluginDefault)?.[key];
    }

    // 4. Try Master English Default
    if (!template) {
      template = this.translationBundles.get('en')?.[key];
    }

    // 5. Fallback to Source Key
    if (!template) {
      return key;
    }

    // Handle ICU Pluralization if {count, plural, ...} is present
    if (typeof params.count === 'number' && template.includes('{count, plural,')) {
      return this.formatIcuPlural(template, params.count, userLang);
    }

    // Standard parameter replacement e.g. {name}
    let formatted = template;
    for (const [k, v] of Object.entries(params)) {
      formatted = formatted.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
    return formatted;
  }

  /**
   * Native Intl ICU MessageFormat Pluralization Formatter.
   */
  private formatIcuPlural(template: string, count: number, langCode: string): string {
    const locale = this.languages.get(langCode)?.defaultLocale || 'en-US';
    const pluralRules = new Intl.PluralRules(locale);
    const category = pluralRules.select(count); // 'zero' | 'one' | 'two' | 'few' | 'many' | 'other'

    // Extract inside {count, plural, ...}
    const match = template.match(/\{count,\s*plural,\s*(.+)\}/);
    if (!match) return template;

    const clauses = match[1];
    // Simple clause parser e.g. one{...} other{...}
    const regex = /([a-z0-9_=]+)\s*\{([^}]+)\}/g;
    const clauseMap: Record<string, string> = {};
    let m;
    while ((m = regex.exec(clauses)) !== null) {
      clauseMap[m[1]] = m[2];
    }

    // Exact count match (e.g. =0) or category match, or fallback to other
    const pattern = clauseMap[`=${count}`] || clauseMap[category] || clauseMap['other'] || '';
    return pattern.replace(/#/g, new Intl.NumberFormat(locale).format(count));
  }

  /**
   * Native Intl Date Formatter.
   */
  public formatDate(date: Date | number, formatStyle: 'short' | 'medium' | 'long' = 'medium'): string {
    const formatter = new Intl.DateTimeFormat(this.userLocaleSettings.locale, {
      dateStyle: formatStyle,
      timeZone: this.userLocaleSettings.timezone,
    });
    return formatter.format(date);
  }

  /**
   * Native Intl Currency Formatter.
   */
  public formatCurrency(amount: number, currencyCode: string = this.userLocaleSettings.currency): string {
    const formatter = new Intl.NumberFormat(this.userLocaleSettings.locale, {
      style: 'currency',
      currency: currencyCode,
    });
    return formatter.format(amount);
  }

  /**
   * Records a multilingual agent interaction trace for telemetry and evaluation.
   */
  public recordAgentTrace(trace: Omit<MultilingualAgentTrace, 'timestamp'>): void {
    this.agentTraces.push({
      ...trace,
      timestamp: Date.now(),
    });
  }

  public getAgentTraces(): readonly MultilingualAgentTrace[] {
    return this.agentTraces;
  }
}

export const globalLanguageRegistryService = LanguageRegistryService.getInstance();
